import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

// @ts-ignore
import "react-toastify/dist/ReactToastify.css";

import { CreateLectureRequest, ParsedError } from "../../interfaces";
import { ActivityButton, SequenceNumberInput } from "../../components/Common";

import { MediaDto, MediaStatusEnum } from "../../interfaces/media";
import { VIDEO_ACCEPT } from "../../constants";
import {
  useGetLectureQuery,
  useAddLectureMutation,
  useUpdateLectureMutation,
  useUploadLectureVideoMutation,
} from "../../store/api/lectureApi";
import { ErrorHandlingTypeEnum, RoleEnum, SequenceEnum } from "../../enums";
import { AppVideoPlayer, AuthorSelect } from "../../components";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { AccountState } from "../../store/slices/accountSlice";

const canChangeLectureAuthor = (role?: string | null) =>
  role === RoleEnum.SUPER_ADMIN ||
  role === RoleEnum.ADMIN ||
  role === RoleEnum.SYSTEM_ADMIN ||
  role === RoleEnum.HEAD_TEACHER_ADMIN;

const formatFileSize = (size?: number | string | null) => {
  const bytes = typeof size === "string" ? Number(size) : size;
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return "";
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    4,
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${
    ["B", "KB", "MB", "GB", "TB"][i]
  }`;
};

const getStatusBadgeColor = (status?: MediaStatusEnum) => {
  switch (status) {
    case MediaStatusEnum.COMPLETED:
      return "success";
    case MediaStatusEnum.PROCESSING:
      return "warning";
    case MediaStatusEnum.FAILED:
      return "danger";
    default:
      return "secondary";
  }
};

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  lessonId: string;
  lessonName?: string;
  subjectId: string;
};
export const AddEditLectureModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  lessonId,
  lessonName,
  subjectId,
}) => {
  const { t, i18n } = useTranslation();
  const [request] = useState<CreateLectureRequest>();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string>();
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string>();
  const [playingSource, setPlayingSource] = useState<
    "original" | "converted" | null
  >(null);
  const [pendingSource, setPendingSource] = useState<
    "original" | "converted" | null
  >(null);
  const [isSwitchingSource, setIsSwitchingSource] = useState(false);
  const [uploadedVideo, setUploadedVideo] = useState<MediaDto | null>(null);
  const [selectedAuthor, setSelectedAuthor] = useState<any>();

  const clearLocalPreview = () => {
    setLocalPreviewUrl((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return undefined;
    });
  };

  const resetVideoUi = () => {
    setUploadProgress(0);
    setUploadingFile(null);
    clearLocalPreview();
    setPlayingVideoUrl(undefined);
    setPlayingSource(null);
    setIsSwitchingSource(false);
    setPendingSource(null);
    setUploadedVideo(null);
  };

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  //#region api
  const {
    data: dataLecture,
    error: errorLecture,
    isError: isErrorLecture,
    isLoading: isLoadingLecture,
    refetch: refetchLecture,
  } = useGetLectureQuery(id as string, {
    skip: !id || !show,
    refetchOnMountOrArgChange: true,
  });

  const [
    addLecture,
    {
      isLoading: isLoadingAdd,
      error: errorAdd,
      isError: isErrorAdd,
      isSuccess: isSuccessAdd,
    },
  ] = useAddLectureMutation();

  const [
    updateLecture,
    {
      isLoading: isLoadingUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
    },
  ] = useUpdateLectureMutation();

  const [
    uploadLectureVideo,
    { isLoading: isLoadingUpload, isError: isErrorUpload, error: errorUpload },
  ] = useUploadLectureVideoMutation();

  //#endregion

  //#region form
  const form = useFormik<CreateLectureRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || lessonName || "",
        ar: request?.name.ar || lessonName || "",
      },
      content: {
        en: request?.content.en || "",
        ar: request?.content.ar || "",
      },
      videoId: request?.videoId,
      lessonId: lessonId,
      sequenceNumber: request?.sequenceNumber || 0,
      authorId: request?.authorId || null,
      role: profile?.role?.key,
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      content: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", {
            displayName: "englishValue",
          }),
        ),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          }),
        ),
      }),
      lessonId: Yup.string(),
      videoId: Yup.string().required(t("isNotEmpty", { displayName: "video" })),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
      authorId: Yup.string()
        .nullable()
        .when("role", {
          is: canChangeLectureAuthor,
          then: (schema) =>
            schema.required(t("isNotEmpty", { displayName: "author" })),
        }),
      role: Yup.string(),
    }),
    onSubmit: async (values) => {
      if (id) {
        await updateLecture({
          id,
          data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
        });
      } else {
        await addLecture({
          ...values,
          sequenceNumber: Number(values.sequenceNumber),
        });
      }
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    if (isLoadingUpload) {
      toast.info(
        t("waitForUpload", {
          defaultValue: "Please wait for the upload to finish",
        }),
      );
      return;
    }
    form.resetForm();
    resetVideoUi();
    onCloseClick(!show);
  };

  const playVideoSource = async (
    source: "original" | "converted",
    url?: string,
  ) => {
    if (!url || isSwitchingSource || isLoadingUpload) return;
    if (playingSource === source && playingVideoUrl === url) return;

    setPendingSource(source);
    setIsSwitchingSource(true);
    try {
      // Warm CloudFront cookies before swapping to HLS — keep current frame visible.
      if (source === "converted" && id) {
        try {
          await refetchLecture().unwrap();
        } catch {
          // Cookies may already be present from an earlier fetch.
        }
      }
      clearLocalPreview();
      setPlayingSource(source);
      setPlayingVideoUrl(url);
    } finally {
      setIsSwitchingSource(false);
      setPendingSource(null);
    }
  };

  const onChangeLectureVideo = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || isLoadingUpload) return;

    if (!file.name.match(/\.(mp4|avi|mkv|mov|wmv|flv|3gp|ogg|mpeg|mpg)$/i)) {
      toast.error(
        t("selectValidVideo", {
          validTypes: "mp4, avi, mkv, mov, wmv, flv, 3gp, ogg, mpeg and mpg",
        }),
        { autoClose: 5000 },
      );
      return;
    }

    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);

    video.preload = "metadata";

    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;

      if (width < 640 || height < 360) {
        URL.revokeObjectURL(objectUrl);
        toast.error(t("videoQualityIsTooLow"), {
          autoClose: 5000,
        });
        return;
      }

      clearLocalPreview();
      setUploadingFile(file);
      setLocalPreviewUrl(objectUrl);
      setPlayingVideoUrl(objectUrl);
      setPlayingSource("original");
      setPendingSource(null);
      setIsSwitchingSource(false);
      setUploadProgress(1);
      setUploadedVideo(null);
      form.setFieldValue("videoId", "");

      uploadLectureVideo({
        file,
        lectureId: id ? dataLecture?.id : undefined,
        onProgress: setUploadProgress,
      })
        .unwrap()
        .then((media) => {
          form.setFieldValue("videoId", media.id);
          form.setFieldTouched("videoId", true, false);
          setUploadedVideo(media);
          setUploadingFile(null);
          setUploadProgress(100);
          toast.success(
            t("videoUploaded", { defaultValue: "Video uploaded successfully" }),
          );

          if (media.path) {
            clearLocalPreview();
            setPlayingSource("original");
            setPlayingVideoUrl(media.path);
          } else if (media.videoUrl) {
            void playVideoSource("converted", media.videoUrl);
          }
        })
        .catch(() => {
          setUploadingFile(null);
          setUploadProgress(0);
          form.setFieldValue("videoId", "");
          toast.error(
            t("videoUploadFailed", {
              defaultValue: "Video upload failed. Please try again.",
            }),
          );
        });
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      toast.error(t("selectValidVideo"));
    };

    video.src = objectUrl;
  };

  const getUploadPhaseLabel = () => {
    if (uploadProgress >= 97) {
      return t("finalizingUpload", { defaultValue: "Finalizing upload…" });
    }
    if (uploadProgress >= 2) {
      return t("uploadingVideo", { defaultValue: "Uploading video…" });
    }
    return t("preparingUpload", { defaultValue: "Preparing upload…" });
  };

  //#endregion

  //#region effects

  useEffect(() => {
    if (id && dataLecture) {
      form.setValues({
        name: dataLecture.name,
        content: dataLecture.content,
        videoId: dataLecture.video?.id,
        lessonId: lessonId,
        sequenceNumber:
          dataLecture?.sequenceNumber || form.values.sequenceNumber || 0,
        authorId: dataLecture.authorId,
        role: profile?.role?.key,
      });
      setSelectedAuthor({
        label:
          i18n.language === "ar"
            ? dataLecture?.author.value.ar
            : dataLecture?.author.value.en,
        value: dataLecture?.author.id,
      });
    }
  }, [dataLecture, isLoadingLecture, id]);

  useEffect(() => {
    if (!show) {
      resetVideoUi();
      return;
    }

    if (!id) {
      resetVideoUi();
      form.resetForm({
        values: {
          name: { en: lessonName || "", ar: lessonName || "" },
          content: { en: "", ar: "" },
          videoId: "",
          lessonId: lessonId,
          sequenceNumber: 0,
          authorId: null,
          role: profile?.role?.key,
        },
      });
      setSelectedAuthor(null);
      return;
    }

    // Switching to a different lecture — clear prior preview until fresh data loads.
    resetVideoUi();
  }, [show, id, lessonId, lessonName]);

  useEffect(() => {
    return () => {
      clearLocalPreview();
    };
  }, []);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (!isLoadingUpload && isErrorUpload) _error = errorUpload as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate, isLoadingUpload]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      onClose();
    }
  }, [isLoadingAdd, isSuccessAdd, isLoadingUpdate, isSuccessUpdate]);

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-lg"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "lecture" })
              : t("addItem", { item: "lecture" })}
          </ModalHeader>
          <ModalBody>
            {(() => {
              const previewVideo =
                uploadedVideo || (id ? dataLecture?.video : undefined);
              const metaName =
                uploadingFile?.name || previewVideo?.name || "";
              const metaSize =
                formatFileSize(uploadingFile?.size) ||
                formatFileSize(previewVideo?.size);
              const showEmptyState = !playingVideoUrl && !isLoadingUpload;

              return (
                <div className="lecture-video-panel mb-3">
                  <div className="lecture-video-panel__stage">
                    {playingVideoUrl ? (
                      <div className="player-wrapper">
                        <AppVideoPlayer
                          src={playingVideoUrl}
                          preservePosition={!localPreviewUrl}
                        />
                      </div>
                    ) : showEmptyState && previewVideo?.path ? (
                      <div className="lecture-video-panel__empty lecture-video-panel__empty--static">
                        <i className="ri-movie-2-line lecture-video-panel__empty-icon" />
                        <span className="lecture-video-panel__empty-title text-truncate px-3">
                          {previewVideo.name || t("video")}
                        </span>
                        <Button
                          color="primary"
                          size="sm"
                          className="mt-1"
                          onClick={() =>
                            void playVideoSource("original", previewVideo.path)
                          }
                        >
                          <i className="ri-play-fill me-1" />
                          {t("playOriginalVideo")}
                        </Button>
                      </div>
                    ) : showEmptyState ? (
                      <label
                        className="lecture-video-panel__empty"
                        htmlFor="lecture-video"
                      >
                        <i className="ri-video-upload-line lecture-video-panel__empty-icon" />
                        <span className="lecture-video-panel__empty-title">
                          {t("uploadVideo")}
                        </span>
                        <span className="lecture-video-panel__empty-hint">
                          {t("videoUploadHint", {
                            defaultValue:
                              "MP4, MOV, MKV and more · minimum 640×360",
                          })}
                        </span>
                      </label>
                    ) : (
                      <div className="lecture-video-panel__empty lecture-video-panel__empty--static">
                        <Spinner color="light" />
                      </div>
                    )}

                    {isLoadingUpload && (
                      <div className="lecture-video-panel__overlay">
                        <div className="lecture-video-panel__progress">
                          <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                            <span className="lecture-video-panel__progress-name text-truncate">
                              {uploadingFile?.name || t("uploadVideo")}
                            </span>
                            <span className="lecture-video-panel__progress-pct">
                              {Math.min(uploadProgress, 100)}%
                            </span>
                          </div>
                          <Progress
                            value={Math.min(uploadProgress, 100)}
                            animated
                            striped
                            color="info"
                            className="lecture-video-panel__progress-bar"
                          />
                          <div className="lecture-video-panel__progress-meta mt-2">
                            <Spinner size="sm" className="me-2" />
                            {getUploadPhaseLabel()}
                            {metaSize ? ` · ${metaSize}` : ""}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="lecture-video-panel__toolbar">
                    {previewVideo?.path && !isLoadingUpload && (
                      <Button
                        color="primary"
                        size="sm"
                        outline={playingSource !== "original"}
                        disabled={isSwitchingSource}
                        onClick={() =>
                          void playVideoSource("original", previewVideo.path)
                        }
                      >
                        {pendingSource === "original" ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="ri-play-circle-line me-1" />
                        )}
                        {t("playOriginalVideo")}
                      </Button>
                    )}
                    {previewVideo?.status === MediaStatusEnum.COMPLETED &&
                      previewVideo.videoUrl &&
                      !isLoadingUpload && (
                        <Button
                          color="secondary"
                          size="sm"
                          outline={playingSource !== "converted"}
                          disabled={isSwitchingSource}
                          onClick={() =>
                            void playVideoSource(
                              "converted",
                              previewVideo.videoUrl,
                            )
                          }
                        >
                          {pendingSource === "converted" ? (
                            <Spinner size="sm" className="me-1" />
                          ) : (
                            <i className="ri-hd-line me-1" />
                          )}
                          {t("playConvertedVideo")}
                        </Button>
                      )}

                    <Label
                      className={`btn btn-sm mb-0 ${
                        isLoadingUpload
                          ? "btn-soft-secondary disabled"
                          : "btn-success"
                      }`}
                      htmlFor="lecture-video"
                    >
                      {isLoadingUpload ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          {t("uploading", { defaultValue: "Uploading" })}
                        </>
                      ) : (
                        <>
                          <i className="ri-video-upload-line me-1" />
                          {previewVideo || playingVideoUrl
                            ? t("replaceVideo", {
                                defaultValue: "Replace video",
                              })
                            : t("uploadVideo")}
                        </>
                      )}
                    </Label>
                    <input
                      disabled={isLoadingUpload || isSwitchingSource}
                      type="file"
                      className="d-none"
                      id="lecture-video"
                      accept={VIDEO_ACCEPT}
                      onChange={onChangeLectureVideo}
                    />
                  </div>

                  {(metaName || previewVideo?.status) && (
                    <div className="lecture-video-panel__meta">
                      {metaName && (
                        <span className="text-truncate" title={metaName}>
                          {metaName}
                        </span>
                      )}
                      {metaSize && (
                        <span className="text-muted">{metaSize}</span>
                      )}
                      {previewVideo?.status && !isLoadingUpload && (
                        <Badge
                          color={getStatusBadgeColor(previewVideo.status)}
                          pill
                        >
                          {t(previewVideo.status, {
                            defaultValue: previewVideo.status,
                          })}
                        </Badge>
                      )}
                      {isLoadingUpload && (
                        <Badge color="info" pill>
                          {t("uploading", { defaultValue: "Uploading" })}
                        </Badge>
                      )}
                    </div>
                  )}

                  {form.touched.videoId && form.errors.videoId ? (
                    <FormFeedback type="invalid" className="d-block mt-2">
                      {form.errors.videoId as string}
                    </FormFeedback>
                  ) : null}
                </div>
              );
            })()}

            <SequenceNumberInput
              tabIndex={1}
              value={form.values.sequenceNumber}
              onChange={(e) => {
                e.preventDefault();
                const { value } = e.target;
                const regex =
                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                if (regex.test(value.toString())) {
                  form.setFieldValue("sequenceNumber", value);
                } else {
                  form.setFieldValue("sequenceNumber", "");
                }
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.sequenceNumber && form.errors.sequenceNumber
                  ? true
                  : false
              }
              sequenceType={SequenceEnum.LECTURE}
              onGenerateSequenceNumber={(v: number) => {
                form.setFieldValue("sequenceNumber", v);
              }}
              mode={id ? "edit" : "add"}
              request={{
                type: SequenceEnum.LECTURE,
                lessonId: lessonId,
              }}
            />
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}
            {canChangeLectureAuthor(profile?.role?.key) && (
              <div className="mb-3">
                <Label htmlFor="authorId" className="form-label">
                  {t("author")}
                  <span className="text-danger">*</span>
                </Label>
                <AuthorSelect
                  placeholder="chooseAuthor"
                  isMulti={false}
                  closeMenuOnSelect={true}
                  value={selectedAuthor}
                  onChange={(v) => {
                    setSelectedAuthor(v);
                    form.setFieldValue("authorId", v.value);
                  }}
                  id="authorId"
                  subjectId={subjectId}
                ></AuthorSelect>
                {form.touched.authorId && form.errors.authorId ? (
                  <FormFeedback type="invalid" className="d-block">
                    {form.errors.authorId as string}
                  </FormFeedback>
                ) : null}
              </div>
            )}
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                autoComplete="off"
                id="name.ar"
                name="name.ar"
                value={form.values.name?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="content.en" className="form-label">
                {t("contentEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ resize: "none" }}
                rows={10}
                autoComplete="off"
                id="content.en"
                name="content.en"
                value={form.values?.content.en}
                type="textarea"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.content?.en && form.errors.content?.en
                    ? true
                    : false
                }
              />
              {form.touched.content?.en && form.errors.content?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.content?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="content.ar" className="form-label">
                {t("contentAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ resize: "none", fontFamily: "aljannat" }}
                rows={10}
                autoComplete="off"
                id="content.ar"
                name="content.ar"
                value={form.values?.content.ar}
                type="textarea"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.content?.ar && form.errors.content?.ar
                    ? true
                    : false
                }
              />
              {form.touched.content?.ar && form.errors.content?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.content?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={
                isLoadingAdd ||
                isLoadingUpdate ||
                isLoadingUpload ||
                isErrorLecture
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
